/**
 * Kopstorm - product card image slider (Dutchies, 3 Aug 2026)
 * ---------------------------------------------------------------------------
 * Turns the product card image into a swipeable slider with dots, using Splide
 * 4.1.4. Splide is served from the theme assets (window.kopSplideSrc, set in
 * theme.liquid) and only fetched once a card is about to enter the viewport, so
 * pages without sliders and shoppers who never scroll to the grid never pay for
 * it. The jsDelivr url is only a fallback if the asset url is missing.
 *
 * Markup comes from snippets/kop-card-slider.liquid. Extra slides already have real
 * src/srcset; on mount we also bump them to eager so they are ready before the swipe.
 */
(function () {
  'use strict';

  var SPLIDE_FALLBACK_SRC = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js';
  var SELECTOR = '[data-kop-card-slider]';
  var splidePromise = null;
  var observer = null;
  var scanTimer = null;

  function loadSplide() {
    if (window.Splide) {
      return Promise.resolve(window.Splide);
    }

    if (splidePromise) {
      return splidePromise;
    }

    splidePromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');

      script.src = window.kopSplideSrc || SPLIDE_FALLBACK_SRC;
      script.async = true;
      script.onload = function () {
        if (window.Splide) {
          resolve(window.Splide);
        } else {
          reject(new Error('Splide did not register'));
        }
      };
      script.onerror = function () {
        reject(new Error('Splide failed to load'));
      };

      document.head.appendChild(script);
    });

    return splidePromise;
  }

  function isMobileOnly(element) {
    return element.getAttribute('data-kop-slider-devices') !== 'all';
  }

  function shouldSkipMount(element) {
    // Mobile/tablet only: do not mount (and therefore no dots) from 700px up.
    return isMobileOnly(element) && window.matchMedia('(min-width: 700px)').matches;
  }

  function preloadSlideImages(element) {
    var images = element.querySelectorAll('.splide__slide img');

    Array.prototype.forEach.call(images, function (img, index) {
      // First slide keeps lazysizes; the rest become eager so swipes never wait on network.
      if (index === 0) {
        return;
      }

      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'low');

      // Force a fetch even if the browser deferred a native lazy image.
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
    });
  }

  function lockTrackHeight(element) {
    var track = element.querySelector('.splide__track');
    var firstImage = element.querySelector('.splide__slide img');

    if (!track || !firstImage) {
      return;
    }

    function apply() {
      var width = firstImage.getBoundingClientRect().width;

      if (width <= 0) {
        return;
      }

      // Prefer the theme aspect-ratio class (e.g. square → 1) so height stays stable
      // even while later slides are still decoding.
      var ratio = 0;
      var styleRatio = window.getComputedStyle(firstImage).aspectRatio;

      if (styleRatio && styleRatio !== 'auto') {
        var parts = styleRatio.split('/').map(function (part) {
          return parseFloat(part.trim(), 10);
        });

        if (parts[0] > 0 && parts[1] > 0) {
          ratio = parts[0] / parts[1];
        } else if (parts[0] > 0) {
          ratio = parts[0];
        }
      }

      if (!ratio) {
        var attrW = parseFloat(firstImage.getAttribute('width'), 10);
        var attrH = parseFloat(firstImage.getAttribute('height'), 10);

        if (attrW > 0 && attrH > 0) {
          ratio = attrW / attrH;
        }
      }

      var height = ratio ? width / ratio : firstImage.getBoundingClientRect().height;

      if (height > 0) {
        track.style.height = Math.round(height) + 'px';
      }
    }

    apply();

    if (!firstImage.complete) {
      firstImage.addEventListener('load', apply, { once: true });
    }

    window.requestAnimationFrame(apply);

    if (typeof ResizeObserver === 'function' && !element.kopHeightObserver) {
      var lastWidth = Math.round(element.getBoundingClientRect().width);

      element.kopHeightObserver = new ResizeObserver(function (entries) {
        var width = Math.round((entries[0] && entries[0].contentRect.width) || element.getBoundingClientRect().width);

        if (width === lastWidth) {
          return;
        }

        lastWidth = width;
        apply();
      });
      element.kopHeightObserver.observe(element);
    }
  }

  function mount(element) {
    if (element.kopSplide || shouldSkipMount(element)) {
      return;
    }

    loadSplide()
      .then(function (Splide) {
        if (element.kopSplide || !element.isConnected || shouldSkipMount(element)) {
          return;
        }

        var showDots = element.getAttribute('data-kop-slider-dots') !== 'false';

        preloadSlideImages(element);

        var options = {
          type: 'slide',
          perPage: 1,
          perMove: 1,
          arrows: false,
          pagination: showDots,
          lazyLoad: false,
          speed: 350,
          rewind: false,
          drag: true,
          waitForTransition: false,
          slideFocus: false,
          autoHeight: false,
          classes: {
            pagination: 'splide__pagination kop-card-slider__dots',
            page: 'splide__pagination__page kop-card-slider__dot'
          }
        };

        // Mobile and tablet only: destroy from 700px up, where the hover image takes over again.
        if (isMobileOnly(element)) {
          options.mediaQuery = 'min';
          options.breakpoints = {
            700: {
              destroy: true
            }
          };
        }

        var instance = new Splide(element, options);

        instance.on('mounted', function () {
          lockTrackHeight(element);
        });

        instance.on('destroy', function () {
          var track = element.querySelector('.splide__track');
          if (track) {
            track.style.height = '';
          }
          if (element.kopHeightObserver) {
            element.kopHeightObserver.disconnect();
            element.kopHeightObserver = null;
          }
        });

        instance.mount();
        element.kopSplide = instance;
        lockTrackHeight(element);
      })
      .catch(function () {
        // Splide unavailable: the card keeps showing its first image, which is the pre-mount state.
        element.removeAttribute('data-kop-slider-observed');
      });
  }

  function getObserver() {
    if (observer || !('IntersectionObserver' in window)) {
      return observer;
    }

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            mount(entry.target);
          }
        });
      },
      { rootMargin: '400px 0px' }
    );

    return observer;
  }

  function scan() {
    var sliders = document.querySelectorAll(SELECTOR + ':not([data-kop-slider-observed])');

    if (!sliders.length) {
      return;
    }

    var io = getObserver();

    Array.prototype.forEach.call(sliders, function (slider) {
      slider.setAttribute('data-kop-slider-observed', 'true');

      if (io) {
        io.observe(slider);
      } else {
        mount(slider);
      }
    });
  }

  function scheduleScan() {
    if (scanTimer) {
      return;
    }

    scanTimer = window.setTimeout(function () {
      scanTimer = null;
      scan();
    }, 100);
  }

  function watchForNewCards() {
    if (!('MutationObserver' in window)) {
      return;
    }

    // Filtering, pagination and app re-renders replace the product list content.
    var mutationObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          scheduleScan();
          return;
        }
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    scan();
    watchForNewCards();
    document.addEventListener('shopify:section:load', scheduleScan);
    document.addEventListener('shopify:section:select', scheduleScan);

    // Mobile-only cards skipped on desktop need a fresh mount when the viewport shrinks.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) {
        window.clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(function () {
        var sliders = document.querySelectorAll(SELECTOR);
        Array.prototype.forEach.call(sliders, function (slider) {
          if (slider.kopSplide) {
            if (shouldSkipMount(slider)) {
              slider.kopSplide.destroy(true);
              slider.kopSplide = null;
              slider.removeAttribute('data-kop-slider-observed');
            }
            return;
          }
          if (!shouldSkipMount(slider)) {
            slider.removeAttribute('data-kop-slider-observed');
          }
        });
        scan();
      }, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
